import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import Lottie from "lottie-react";
import animationData from "./data2.json";

const ScrollControlledLottie = forwardRef(({ activeIndex = 0 } , ref) => {
  const lottieRef = useRef(null);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const currentFrameRef = useRef(0);
  const rafRef = useRef(null);
  const animStartRef = useRef(null);
  const animFromRef = useRef(0);
  const animToRef = useRef(0);

  // const startWhenVisiblePercent = 90;
  // const completeWhenTopAt = -70;

  useEffect(() => {
    // Only need a cleanup for any pending RAF when the component unmounts
    return () => {
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // compute total frames from animation data
  const totalFrames =
    (animationData && (animationData.op || animationData.frames)) || 221;

  // easing function
  const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

  const smoothToFrame = (toFrame, duration = 2000) => {
    cancelAnimationFrame(rafRef.current);
    animFromRef.current = Math.max(0, Math.min(totalFrames, currentFrameRef.current));
    animToRef.current = Math.max(0, Math.min(totalFrames, toFrame));
    animStartRef.current = null;

    const step = (timestamp) => {
      if (!animStartRef.current) animStartRef.current = timestamp;
      const elapsed = timestamp - animStartRef.current;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeInOutQuad(progress);
      const frame = animFromRef.current + (animToRef.current - animFromRef.current) * eased;
      try {
        if (lottieRef.current) lottieRef.current.goToAndStop(frame, true);
      } catch (err) {
        /* ignore */
      }
      currentFrameRef.current = frame;
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };

    rafRef.current = requestAnimationFrame(step);
  };

  // clicking toggles between start and end frames
  const handleClick = () => {
    if (!isReady) return;
    const mid = totalFrames / 2;
    const target = currentFrameRef.current < mid ? totalFrames : 0;
    smoothToFrame(target, 1200);
  };

  // expose imperative methods to parent via ref
  useImperativeHandle(ref, () => ({
    playToEnd: (duration = 1200) => {
      if (!isReady) return;
      smoothToFrame(totalFrames, duration);
    },
    playToStart: (duration = 1200) => {
      if (!isReady) return;
      smoothToFrame(0, duration);
    },
    toggle: (duration = 1200) => {
      if (!isReady) return;
      const mid = totalFrames / 2;
      const target = currentFrameRef.current < mid ? totalFrames : 0;
      smoothToFrame(target, duration);
    },
  }));

  return (
    <div
      ref={containerRef}
      className="place3"
      style={{ marginTop: -202, cursor: "pointer" }}
      onClick={handleClick}
    >
      <div className="place3In">
        <Lottie
          lottieRef={lottieRef}
          animationData={animationData}
          autoplay={false}
          loop={false}
          onDOMLoaded={() => {
            setTimeout(() => {
              setIsReady(true);
            }, 100);
          }}
        />
      </div>
    </div>
  );
});

export default ScrollControlledLottie;
